/**
 * Utilitaires pour détecter le navigateur et générer des instructions spécifiques
 * pour configurer les cookies tiers avec Keycloak
 */

/**
 * Détecte le navigateur de l'utilisateur
 */
export const detectBrowser = () => {
    const userAgent = navigator.userAgent;
    const browserInfo = {
        name: 'unknown',
        version: '',
        isChrome: /Chrome/.test(userAgent) && !/Edg/.test(userAgent),
        isFirefox: /Firefox/.test(userAgent),
        isSafari: /Safari/.test(userAgent) && !/Chrome/.test(userAgent),
        isEdge: /Edg/.test(userAgent),
        isOpera: /OPR/.test(userAgent),
        isBrave: false // Will be detected below
    };

    if (browserInfo.isChrome) {
        browserInfo.name = 'Chrome';
        // Check if it's Brave (Brave uses Chrome's user agent)
        if (navigator.brave && navigator.brave.isBrave) {
            browserInfo.name = 'Brave';
            browserInfo.isBrave = true;
            browserInfo.isChrome = false;
        }
    } else if (browserInfo.isFirefox) {
        browserInfo.name = 'Firefox';
    } else if (browserInfo.isSafari) {
        browserInfo.name = 'Safari';
    } else if (browserInfo.isEdge) {
        browserInfo.name = 'Edge';
    } else if (browserInfo.isOpera) {
        browserInfo.name = 'Opera';
    }

    // Extract version
    try {
        if (browserInfo.isChrome || browserInfo.isBrave) {
            const match = userAgent.match(/Chrome\/(\d+)/);
            browserInfo.version = match ? match[1] : '';
        } else if (browserInfo.isFirefox) {
            const match = userAgent.match(/Firefox\/(\d+)/);
            browserInfo.version = match ? match[1] : '';
        } else if (browserInfo.isSafari) {
            const match = userAgent.match(/Version\/(\d+)/);
            browserInfo.version = match ? match[1] : '';
        } else if (browserInfo.isEdge) {
            const match = userAgent.match(/Edg\/(\d+)/);
            browserInfo.version = match ? match[1] : '';
        }
    } catch (error) {
        console.warn('Erreur détection version navigateur:', error);
    }

    // console.log('🌐 [BrowserDetection] Navigateur détecté:', browserInfo);
    return browserInfo;
};

/**
 * Obtient le domaine Keycloak à partir de l'URL actuelle ou de la configuration
 */
export const getKeycloakDomain = () => {
    // Essayer de détecter depuis l'URL courante si on a des paramètres Keycloak
    const urlParams = new URLSearchParams(window.location.search);
    const issuer = urlParams.get('iss');
    
    if (issuer) {
        try {
            const url = new URL(issuer);
            // console.log('🔐 [KeycloakDomain] Détecté depuis URL:', url.hostname);
            return url.hostname;
        } catch (e) {
            console.warn('Erreur parsing issuer URL:', e);
        }
    }

    // Fallback: essayer de détecter depuis les cookies existants ou configuration
    // Vous pouvez ajuster cette logique selon votre configuration
    const knownDomains = [
        'keycloak.synapse42.com',
        'auth.kubrik21.com',
        'auth.localhost'
    ];

    // Retourner le premier domaine connu ou un domaine par défaut
    // console.log('🔐 [KeycloakDomain] Utilisation du domaine par défaut');
    return knownDomains[0];
};

/**
 * Génère les instructions spécifiques pour chaque navigateur
 */
export const getBrowserSpecificInstructions = (keycloakDomain, t) => {
    const browser = detectBrowser();
    const domain = keycloakDomain || getKeycloakDomain();
    
    const browserKey = browser.name.toLowerCase();
    
    // Get translated instructions if t function is provided
    if (t) {
        try {
            const browserInstructions = t(`error.cookie.browserInstructions.${browserKey}`, { 
                domain,
                returnObjects: true 
            });
            
            // If specific browser translation not found, fallback to unknown
            const instructions = browserInstructions || t('error.cookie.browserInstructions.unknown', { 
                domain,
                returnObjects: true 
            });
            
            // Add metadata for each browser type
            const quickActions = {
                chrome: { quickAction: 'chrome://settings/content/cookies', canAutoOpen: true },
                firefox: { quickAction: 'about:preferences#privacy', canAutoOpen: true },
                safari: { quickAction: null, canAutoOpen: false },
                edge: { quickAction: 'edge://settings/content/cookies', canAutoOpen: true },
                brave: { quickAction: 'brave://settings/shields', canAutoOpen: true },
                opera: { quickAction: 'opera://settings/content/cookies', canAutoOpen: true },
                unknown: { quickAction: null, canAutoOpen: false }
            };
            
            const metadata = quickActions[browserKey] || quickActions.unknown;
            
            return {
                browser,
                domain,
                instructions: {
                    ...instructions,
                    ...metadata,
                    icon: '🛠️' // Default icon, can be customized per browser if needed
                }
            };
        } catch (error) {
            console.warn('Translation not available, falling back to hardcoded instructions:', error);
        }
    }
    
    // Fallback to hardcoded instructions if no translation function provided
    const instructions = {
        chrome: {
            title: 'Chrome / Chromium',
            steps: [
                'Cliquez sur l\'icône de verrou 🔒 dans la barre d\'adresse',
                'Sélectionnez "Paramètres du site"',
                'Faites défiler jusqu\'à "Cookies"',
                'Sélectionnez "Autoriser" pour les cookies',
                `Ajoutez une exception pour [*.]${domain}`,
                'Rechargez la page'
            ],
            quickAction: 'chrome://settings/content/cookies',
            canAutoOpen: true
        },
        firefox: {
            title: 'Firefox',
            steps: [
                'Cliquez sur l\'icône de bouclier 🛡️ dans la barre d\'adresse',
                'Cliquez sur "Protection renforcée contre le pistage activée"',
                'Sélectionnez "Désactiver la protection pour ce site"',
                'Ou allez dans Paramètres → Vie privée et sécurité',
                'Sous "Cookies", sélectionnez "Standard"',
                'Rechargez la page'
            ],
            quickAction: 'about:preferences#privacy',
            canAutoOpen: true
        },
        safari: {
            title: 'Safari',
            steps: [
                'Ouvrez Safari → Préférences (⌘ + ,)',
                'Allez dans l\'onglet "Confidentialité"',
                'Décochez "Empêcher le suivi inter-sites"',
                'Décochez "Bloquer tous les cookies"',
                `Ajoutez ${domain} aux sites de confiance`,
                'Rechargez la page'
            ],
            quickAction: null,
            canAutoOpen: false
        },
        edge: {
            title: 'Microsoft Edge',
            steps: [
                'Cliquez sur ⋯ → Paramètres',
                'Allez dans "Cookies et autorisations de site"',
                'Cliquez sur "Cookies et données de sites stockées"',
                'Sélectionnez "Autoriser les cookies"',
                `Ajoutez une exception pour [*.]${domain}`,
                'Rechargez la page'
            ],
            quickAction: 'edge://settings/content/cookies',
            canAutoOpen: true
        },
        brave: {
            title: 'Brave Browser',
            steps: [
                'Cliquez sur l\'icône Brave (lion) dans la barre d\'adresse',
                'Cliquez sur "Paramètres des boucliers avancés"',
                'Désactivez "Bloquer les cookies inter-sites"',
                'Ou ajoutez ce site aux exceptions',
                `Autorisez spécifiquement ${domain}`,
                'Rechargez la page'
            ],
            quickAction: 'brave://settings/shields',
            canAutoOpen: true
        },
        opera: {
            title: 'Opera',
            steps: [
                'Cliquez sur ⋯ → Paramètres (Alt + P)',
                'Allez dans "Vie privée et sécurité"',
                'Cliquez sur "Paramètres du site"',
                'Sélectionnez "Cookies"',
                'Choisissez "Autoriser tous les cookies"',
                'Rechargez la page'
            ],
            quickAction: 'opera://settings/content/cookies',
            canAutoOpen: true
        },
        unknown: {
            title: 'Navigateur non reconnu',
            steps: [
                'Recherchez les paramètres de confidentialité/vie privée',
                'Trouvez les paramètres des cookies',
                'Autorisez les cookies tiers',
                `Ajoutez ${domain} aux exceptions si possible`,
                'Rechargez la page'
            ],
            quickAction: null,
            canAutoOpen: false
        }
    };

    const browserInstructions = instructions[browserKey] || instructions.unknown;
    
    return {
        browser,
        domain,
        instructions: browserInstructions
    };
};

/**
 * Copie les instructions dans le presse-papiers
 */
// export const copyInstructionsToClipboard = async (domain) => {
//     const { browser, instructions } = getBrowserSpecificInstructions(domain);
    
//     const text = `Instructions pour ${instructions.title}:\n\n` +
//                 instructions.steps.map((step, index) => `${index + 1}. ${step}`).join('\n') +
//                 `\n\nDomaine à autoriser: ${domain || getKeycloakDomain()}`;
    
//     try {
//         await navigator.clipboard.writeText(text);
//         // console.log('📋 [Clipboard] Instructions copiées');
//         return true;
//     } catch (error) {
//         console.error('❌ [Clipboard] Erreur copie:', error);
        
//         // Fallback pour les navigateurs plus anciens
//         try {
//             const textArea = document.createElement('textarea');
//             textArea.value = text;
//             textArea.style.position = 'fixed';
//             textArea.style.left = '-999999px';
//             textArea.style.top = '-999999px';
//             document.body.appendChild(textArea);
//             textArea.focus();
//             textArea.select();
            
//             const success = document.execCommand('copy');
//             document.body.removeChild(textArea);
            
//             if (success) {
//                 console.log('📋 [Clipboard] Instructions copiées (fallback)');
//                 return true;
//             }
//         } catch (fallbackError) {
//             console.error('❌ [Clipboard] Erreur fallback:', fallbackError);
//         }
        
//         return false;
//     }
// };